// Cadence › Issues (`/c/{slug}/cadence/issues`; 06 §Cadence "Issues"): the issues lists the
// caller may see as one board, identify → discuss → solve.
//   - A list picker (each team the caller sees, the company's list, or all) in the URL (`?team=`).
//   - Cards are dragged between and within columns (or moved with Alt + arrow keys); a move is on
//     screen before the server answers (PATCH `status` + `position`) and rolls back if refused.
//   - Each card: the title, the owner (seat · holder), the priority, the age, what it was raised
//     from, and "Escalated" when it went up to the company; on hover Edit, Escalate (or Hand
//     back), Solve, Archive and Delete — each only when the server says the caller may.
//   - "New issue" (header, and the Identify column's "+"); "Solved recently" (the last 30 days)
//     opens under the board; the Archived view lists archived issues with Restore and Delete.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AvatarTile,
  Badge,
  Button,
  cn,
  EmptyState,
  LocationHeader,
  NativeSelect,
  NativeSelectOption,
  Segmented,
  Toolbar,
} from "@companyos/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  ArchiveIcon,
  ArchiveRestoreIcon,
  ArrowDownToLineIcon,
  ArrowUpFromLineIcon,
  CheckCircle2Icon,
  ChevronRightIcon,
  CircleDotIcon,
  LinkIcon,
  PencilIcon,
  PlusIcon,
  RotateCcwIcon,
  TelescopeIcon,
  Trash2Icon,
} from "lucide-react";
import { type DragEvent, type KeyboardEvent, useId, useState } from "react";
import type { Api } from "../../api/client";
import { useOptimisticMutation } from "../../api/optimistic";
import type { IssueBoard, IssueView } from "../../api/types";
import type { CompanyAccess } from "../company";
import { Loading } from "../screen-parts";
import { ErrorNotice } from "../structure/parts";
import {
  ageLabel,
  COLUMN_HINTS,
  COLUMN_LABELS,
  columnsOf,
  ISSUE_COLUMNS,
  type IssueColumn,
  moveIssue,
  ownerLabel,
  PRIORITY_BADGE,
  PRIORITY_LABELS,
} from "./cadence-model";
import { HOVER_STRIP, IconAction } from "./cadence-parts";
import { cadenceKeys, issuesQuery } from "./cadence-queries";
import { IssueDialog, SolveDialog } from "./issue-dialogs";

export type IssuesView = "board" | "archived";

type Patch = Parameters<Api["client"]["issues"]["update"]>[0]["body"];

const matchesTeam = (i: IssueView, team: string) =>
  team === "all"
    ? true
    : team === "company"
      ? i.team_id === null || i.escalated_to !== "team"
      : i.team_id === team;

export function IssuesScreen({
  api,
  company,
  team,
  view,
  onSearch,
}: {
  api: Api;
  company: CompanyAccess;
  /** A team id, `company`, or `all`. */
  team: string;
  view: IssuesView;
  onSearch: (next: { team?: string | undefined; view?: IssuesView | undefined }) => void;
}) {
  const co = company.id;
  const qc = useQueryClient();
  const board = useQuery(issuesQuery(api, co));
  const [composer, setComposer] = useState(false);
  const [editing, setEditing] = useState<IssueView | null>(null);
  const [solving, setSolving] = useState<IssueView | null>(null);
  const [deleting, setDeleting] = useState<IssueView | null>(null);
  const [solvedOpen, setSolvedOpen] = useState(false);
  const pickId = useId();

  const liveKey = cadenceKeys.issueList(co, "live");
  const patchLive = (fn: (rows: IssueView[]) => IssueView[]) =>
    qc.setQueryData<IssueBoard>(liveKey, (b) => (b ? { ...b, data: fn(b.data) } : b));
  const settle = () => [cadenceKeys.issues(co), cadenceKeys.root(co)];
  const move = useOptimisticMutation({
    keys: () => [liveKey],
    apply: (v: { id: string; to: IssueColumn; index: number }) =>
      patchLive((rows) => moveIssue(rows, v.id, v.to, v.index)),
    mutationFn: (v) =>
      api.client.issues.update({
        params: { co, id: v.id },
        body: { status: v.to, position: v.index },
      }),
    settle,
  });
  const update = useOptimisticMutation({
    keys: () => [cadenceKeys.issues(co)],
    apply: (v: { issue: IssueView; body: Patch; hide?: boolean }) =>
      patchLive((rows) =>
        v.hide
          ? rows.filter((r) => r.id !== v.issue.id)
          : rows.map((r) =>
              r.id === v.issue.id ? { ...r, ...(v.body as Partial<IssueView>) } : r,
            ),
      ),
    mutationFn: (v) => api.client.issues.update({ params: { co, id: v.issue.id }, body: v.body }),
    settle,
  });
  const escalate = useOptimisticMutation({
    keys: () => [liveKey],
    apply: (v: { issue: IssueView; to: "company" | "team" | "portfolio" }) =>
      patchLive((rows) =>
        rows.map((r) => (r.id === v.issue.id ? { ...r, escalated_to: v.to } : r)),
      ),
    mutationFn: (v) =>
      api.client.issues.escalate({ params: { co, id: v.issue.id }, body: { to: v.to } }),
    settle,
  });
  const remove = useOptimisticMutation({
    keys: () => [cadenceKeys.issues(co)],
    apply: (i: IssueView) => {
      for (const k of ["live", "closed", "archived"] as const)
        qc.setQueryData<IssueBoard>(cadenceKeys.issueList(co, k), (b) =>
          b ? { ...b, data: b.data.filter((r) => r.id !== i.id) } : b,
        );
    },
    mutationFn: (i) => api.client.issues.delete({ params: { co, id: i.id } }),
    settle,
  });

  const data = board.data;
  const teams = data?.teams ?? [];
  const shown = (data?.data ?? []).filter((i) => matchesTeam(i, team));
  const columns = columnsOf(shown);
  const canRaise = !!data && (data.can_raise_company || teams.some((t) => t.can_raise));
  const teamName =
    team === "all" ? null : team === "company" ? "Company" : teams.find((t) => t.id === team)?.name;

  const actions: CardActions = {
    edit: setEditing,
    solve: setSolving,
    escalate: (i) =>
      escalate.mutate({ issue: i, to: i.escalated_to === "company" ? "team" : "company" }),
    // FOUNDER-OS: up to the account's Founder OS, and back down to the company.
    portfolio: (i) =>
      escalate.mutate({ issue: i, to: i.escalated_to === "portfolio" ? "company" : "portfolio" }),
    archive: (i) => update.mutate({ issue: i, body: { archived: true }, hide: true }),
    remove: setDeleting,
    move: (i, to, index) => move.mutate({ id: i.id, to, index }),
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col" data-slot="issues">
      <LocationHeader
        icon={CircleDotIcon}
        title="Issues"
        badge={teamName ? <Badge variant="accent">{teamName}</Badge> : null}
        actions={
          canRaise ? (
            <Button size="sm" onClick={() => setComposer(true)}>
              <PlusIcon data-icon="inline-start" aria-hidden="true" />
              <span className="max-sm:sr-only">New issue</span>
            </Button>
          ) : null
        }
      />
      <Toolbar bleed aria-label="Issues view">
        <label htmlFor={pickId} className="sr-only">
          List
        </label>
        <NativeSelect
          id={pickId}
          size="sm"
          value={team}
          onChange={(e) =>
            onSearch({ team: e.target.value === "all" ? undefined : e.target.value })
          }
          className="w-48"
        >
          <NativeSelectOption value="all">All lists</NativeSelectOption>
          {teams.map((t) => (
            <NativeSelectOption key={t.id} value={t.id}>
              {t.name}
            </NativeSelectOption>
          ))}
          <NativeSelectOption value="company">Company</NativeSelectOption>
        </NativeSelect>
        <Segmented
          size="sm"
          aria-label="Show"
          value={view}
          onValueChange={(v) => onSearch({ view: v === "board" ? undefined : v })}
          options={[
            { value: "board", label: "Board" },
            { value: "archived", label: "Archived" },
          ]}
        />
        <span className="ml-auto text-xs text-muted-foreground tabular-nums max-md:hidden">
          {shown.length} open {shown.length === 1 ? "issue" : "issues"}
        </span>
      </Toolbar>

      <div className="min-h-0 flex-1 overflow-auto px-6 pt-4 pb-10 max-md:px-4">
        {board.isPending ? (
          <Loading label="Loading issues" rows={6} />
        ) : board.isError ? (
          <ErrorNotice what="Issues" error={board.error} />
        ) : view === "archived" ? (
          <ArchivedList api={api} co={co} team={team} onRemove={setDeleting} />
        ) : (
          <>
            <div
              className="grid min-w-[720px] grid-cols-3 gap-4 max-md:min-w-0 max-md:grid-cols-1"
              data-slot="issues-board"
            >
              {ISSUE_COLUMNS.map((col) => (
                <Column
                  key={col}
                  column={col}
                  issues={columns[col]}
                  actions={actions}
                  onAdd={col === "identify" && canRaise ? () => setComposer(true) : undefined}
                />
              ))}
            </div>
            <SolvedRecently
              api={api}
              co={co}
              team={team}
              open={solvedOpen}
              onOpenChange={setSolvedOpen}
              onReopen={(i) => move.mutate({ id: i.id, to: "identify", index: 0 })}
            />
          </>
        )}
      </div>

      {composer ? (
        <IssueDialog
          api={api}
          co={co}
          open={composer}
          onOpenChange={setComposer}
          defaults={team === "all" ? undefined : { team_id: team === "company" ? null : team }}
        />
      ) : null}
      {editing ? (
        <IssueDialog
          api={api}
          co={co}
          open={!!editing}
          issue={editing}
          onOpenChange={(o) => !o && setEditing(null)}
        />
      ) : null}
      {solving ? (
        <SolveDialog
          api={api}
          co={co}
          issue={solving}
          open={!!solving}
          onOpenChange={(o) => !o && setSolving(null)}
        />
      ) : null}
      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent size="sm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{deleting?.title}”?</AlertDialogTitle>
            <AlertDialogDescription>
              The issue is removed for good. Archive it instead to keep the record.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (deleting) remove.mutate(deleting);
                setDeleting(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type CardActions = {
  edit: (i: IssueView) => void;
  solve: (i: IssueView) => void;
  escalate: (i: IssueView) => void;
  portfolio: (i: IssueView) => void;
  archive: (i: IssueView) => void;
  remove: (i: IssueView) => void;
  move: (i: IssueView, to: IssueColumn, index: number) => void;
};

const DRAG_TYPE = "application/x-companyos-issue";
/** The card being dragged (the drop reads it: a drag carries only its id). */
let dragged: IssueView | null = null;

function Column({
  column,
  issues,
  actions,
  onAdd,
}: {
  column: IssueColumn;
  issues: IssueView[];
  actions: CardActions;
  onAdd: (() => void) | undefined;
}) {
  const [over, setOver] = useState<number | null>(null);
  /** The drop index from the pointer: the cards whose middle is above it (the dragged one aside). */
  const indexAt = (e: DragEvent<HTMLElement>) => {
    const cards = [
      ...e.currentTarget.querySelectorAll<HTMLElement>('[data-slot="issue-card"]'),
    ].filter((el) => el.dataset.dragging !== "true");
    return cards.filter((el) => {
      const r = el.getBoundingClientRect();
      return r.top + r.height / 2 < e.clientY;
    }).length;
  };
  const label = COLUMN_LABELS[column];
  return (
    <section
      aria-label={`${label} (${issues.length})`}
      data-slot="issue-column"
      data-column={column}
      className={cn(
        "flex min-h-40 min-w-0 flex-col rounded-lg border border-subtle bg-sunken/60 p-2",
        over !== null && "border-primary/40 bg-primary-soft/30",
      )}
      onDragOver={(e) => {
        if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setOver(indexAt(e));
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null);
      }}
      onDrop={(e) => {
        const id = e.dataTransfer.getData(DRAG_TYPE);
        const index = indexAt(e);
        setOver(null);
        const issue = dragged;
        if (!id || !issue || issue.id !== id) return;
        e.preventDefault();
        if (issue.status === column && issues.findIndex((i) => i.id === id) === index) return;
        actions.move(issue, column, index);
      }}
    >
      <header className="flex h-8 items-center gap-2 px-1.5">
        <h2 className="text-sm font-semibold text-foreground">{label}</h2>
        <span className="text-xs text-faint tabular-nums">{issues.length}</span>
        <span className="min-w-0 truncate text-xs text-muted-foreground max-lg:hidden">
          {COLUMN_HINTS[column]}
        </span>
        {onAdd ? (
          <span className="ml-auto">
            <IconAction label="New issue" tip="New issue" onClick={onAdd}>
              <PlusIcon aria-hidden="true" />
            </IconAction>
          </span>
        ) : null}
      </header>
      <ul className="flex flex-1 flex-col gap-2 pt-1">
        {issues.map((i, n) => (
          <li key={i.id} className="relative">
            {over === n ? <DropLine /> : null}
            <IssueCard
              issue={i}
              index={n}
              column={column}
              count={issues.length}
              actions={actions}
            />
          </li>
        ))}
        {over !== null && over >= issues.length ? (
          <li className="relative h-2">
            <DropLine />
          </li>
        ) : null}
        {!issues.length && over === null ? (
          <li className="px-2 py-6 text-center text-xs text-faint">Nothing here</li>
        ) : null}
      </ul>
    </section>
  );
}

const DropLine = () => (
  <span
    aria-hidden="true"
    className="absolute -top-1.5 right-1 left-1 h-0.5 rounded-full bg-primary"
  />
);

function IssueCard({
  issue: i,
  index,
  column,
  count,
  actions,
}: {
  issue: IssueView;
  index: number;
  column: IssueColumn;
  count: number;
  actions: CardActions;
}) {
  const [dragging, setDragging] = useState(false);
  const owner = ownerLabel(i);
  const col = ISSUE_COLUMNS.indexOf(column);
  const onKey = (e: KeyboardEvent<HTMLElement>) => {
    if (!i.can_edit || !e.altKey || e.target !== e.currentTarget) return;
    const left = ISSUE_COLUMNS[col - 1];
    const right = ISSUE_COLUMNS[col + 1];
    if (e.key === "ArrowLeft" && left) actions.move(i, left, 0);
    else if (e.key === "ArrowRight" && right) actions.move(i, right, 0);
    else if (e.key === "ArrowUp" && index > 0) actions.move(i, column, index - 1);
    else if (e.key === "ArrowDown" && index < count - 1) actions.move(i, column, index + 1);
    else return;
    e.preventDefault();
  };
  return (
    <article
      // biome-ignore lint/a11y/noNoninteractiveTabindex: a focusable card moved with Alt + arrows
      tabIndex={0}
      draggable={i.can_edit}
      data-slot="issue-card"
      data-issue={i.id}
      data-dragging={dragging || undefined}
      aria-label={i.title}
      aria-description={i.can_edit ? "Alt + arrow keys move the card" : undefined}
      onKeyDown={onKey}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, i.id);
        e.dataTransfer.effectAllowed = "move";
        dragged = i;
        setDragging(true);
      }}
      onDragEnd={() => {
        dragged = null;
        setDragging(false);
      }}
      className={cn(
        "group/row relative rounded-md border border-border bg-card p-2.5 shadow-xs outline-none focus-visible:ring-2 focus-visible:ring-ring",
        i.can_edit && "cursor-grab active:cursor-grabbing",
        dragging && "opacity-40",
      )}
    >
      <div className="flex items-center gap-1.5">
        <Badge variant={PRIORITY_BADGE[i.priority] ?? "neutral"}>
          {PRIORITY_LABELS[i.priority] ?? i.priority}
        </Badge>
        {i.escalated_to === "company" ? <Badge variant="warning">Escalated</Badge> : null}
        {i.escalated_to === "portfolio" ? <Badge variant="danger">Portfolio</Badge> : null}
        {i.team_name === null && i.escalated_to === "team" ? (
          <Badge variant="outline">Company</Badge>
        ) : null}
        <span
          className="ml-auto text-xs text-muted-foreground tabular-nums"
          title={`Raised ${i.age_days} ${i.age_days === 1 ? "day" : "days"} ago`}
          data-slot="issue-age"
        >
          {ageLabel(i.age_days)}
        </span>
      </div>
      <p className="mt-1.5 line-clamp-2 text-sm font-medium text-foreground">{i.title}</p>
      {owner ? (
        <p className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
          <AvatarTile name={i.owner_name ?? i.owner_title ?? "?"} kind="person" size="xs" />
          <span className="truncate">{owner}</span>
        </p>
      ) : null}
      {i.source_label ? (
        <p
          className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-faint"
          data-slot="issue-source"
        >
          <LinkIcon aria-hidden="true" className="size-3 shrink-0" />
          <span className="truncate">{i.source_label}</span>
        </p>
      ) : null}
      {i.can_edit || i.can_escalate ? (
        <div
          className={cn(
            HOVER_STRIP,
            "absolute top-1.5 right-1.5 rounded-md border border-subtle bg-card p-0.5 shadow-xs",
          )}
        >
          {i.can_edit ? (
            <IconAction label={`Edit ${i.title}`} tip="Edit" onClick={() => actions.edit(i)}>
              <PencilIcon aria-hidden="true" />
            </IconAction>
          ) : null}
          {i.can_escalate &&
          i.escalated_to !== "portfolio" &&
          (i.team_id !== null || i.escalated_to === "company") ? (
            i.escalated_to === "company" && i.team_id !== null ? (
              <IconAction
                label={`Hand ${i.title} back to the team`}
                tip="Hand back to the team"
                onClick={() => actions.escalate(i)}
              >
                <ArrowDownToLineIcon aria-hidden="true" />
              </IconAction>
            ) : i.escalated_to !== "company" ? (
              <IconAction
                label={`Escalate ${i.title} to the company`}
                tip="Escalate to the company"
                onClick={() => actions.escalate(i)}
              >
                <ArrowUpFromLineIcon aria-hidden="true" />
              </IconAction>
            ) : null
          ) : null}
          {/* FOUNDER-OS: a company-level issue goes up to the portfolio; back with Hand back. */}
          {i.can_escalate && i.escalated_to === "portfolio" ? (
            <IconAction
              label={`Hand ${i.title} back to the company`}
              tip="Hand back to company"
              onClick={() => actions.portfolio(i)}
            >
              <ArrowDownToLineIcon aria-hidden="true" />
            </IconAction>
          ) : i.can_escalate && (i.team_id === null || i.escalated_to === "company") ? (
            <IconAction
              label={`Escalate ${i.title} to portfolio`}
              tip="Escalate to portfolio"
              onClick={() => actions.portfolio(i)}
            >
              <TelescopeIcon aria-hidden="true" />
            </IconAction>
          ) : null}
          {i.can_edit ? (
            <>
              <IconAction label={`Solve ${i.title}`} tip="Solve" onClick={() => actions.solve(i)}>
                <CheckCircle2Icon aria-hidden="true" />
              </IconAction>
              <IconAction
                label={`Archive ${i.title}`}
                tip="Archive"
                onClick={() => actions.archive(i)}
              >
                <ArchiveIcon aria-hidden="true" />
              </IconAction>
              <IconAction
                label={`Delete ${i.title}`}
                tip="Delete"
                danger
                onClick={() => actions.remove(i)}
              >
                <Trash2Icon aria-hidden="true" />
              </IconAction>
            </>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

const RESOLUTION: Record<string, string> = {
  task: "To-dos",
  decision: "Decision",
  none: "Closed",
};

function SolvedRecently({
  api,
  co,
  team,
  open,
  onOpenChange,
  onReopen,
}: {
  api: Api;
  co: string;
  team: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onReopen: (i: IssueView) => void;
}) {
  const closed = useQuery({ ...issuesQuery(api, co, "closed"), enabled: open });
  const rows = (closed.data?.data ?? []).filter(
    (i) => (i.status === "resolved" || i.status === "dropped") && matchesTeam(i, team),
  );
  const listId = useId();
  return (
    <section className="mt-6" data-slot="solved-recently">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => onOpenChange(!open)}
        className="flex items-center gap-1.5 text-sm font-semibold text-foreground hover:underline"
      >
        <ChevronRightIcon
          aria-hidden="true"
          className={cn("size-4 transition-transform", open && "rotate-90")}
        />
        Solved recently
        {closed.data ? (
          <span className="text-xs font-normal text-faint tabular-nums">{rows.length}</span>
        ) : null}
      </button>
      {open ? (
        <div id={listId} className="mt-2">
          {closed.isPending ? (
            <Loading label="Loading solved issues" rows={2} />
          ) : rows.length ? (
            <ul className="divide-y divide-subtle rounded-md border border-subtle">
              {rows.map((i) => (
                <li key={i.id} className="group/row flex h-10 items-center gap-3 px-3 text-sm">
                  <CheckCircle2Icon
                    aria-hidden="true"
                    className="size-4 shrink-0 text-success-text"
                  />
                  <span className="min-w-0 flex-1 truncate">{i.title}</span>
                  <Badge variant={i.status === "dropped" ? "neutral" : "success"}>
                    {i.status === "dropped" ? "Dropped" : RESOLUTION[i.resolution_kind ?? "none"]}
                  </Badge>
                  <span className="w-28 truncate text-xs text-muted-foreground max-md:hidden">
                    {i.team_name ?? "Company"}
                  </span>
                  <span className="w-12 text-right text-xs text-muted-foreground tabular-nums">
                    {ageLabel(i.age_days)}
                  </span>
                  <span className={HOVER_STRIP}>
                    {i.can_edit ? (
                      <IconAction
                        label={`Reopen ${i.title}`}
                        tip="Reopen"
                        onClick={() => onReopen(i)}
                      >
                        <RotateCcwIcon aria-hidden="true" />
                      </IconAction>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-faint">Nothing solved in the last 30 days.</p>
          )}
        </div>
      ) : null}
    </section>
  );
}

function ArchivedList({
  api,
  co,
  team,
  onRemove,
}: {
  api: Api;
  co: string;
  team: string;
  onRemove: (i: IssueView) => void;
}) {
  const qc = useQueryClient();
  const list = useQuery(issuesQuery(api, co, "archived"));
  const restore = useOptimisticMutation({
    keys: () => [cadenceKeys.issueList(co, "archived")],
    apply: (i: IssueView) =>
      qc.setQueryData<IssueBoard>(cadenceKeys.issueList(co, "archived"), (b) =>
        b ? { ...b, data: b.data.filter((r) => r.id !== i.id) } : b,
      ),
    mutationFn: (i) =>
      api.client.issues.update({ params: { co, id: i.id }, body: { archived: false } }),
    settle: () => [cadenceKeys.issues(co)],
  });
  const rows = (list.data?.data ?? []).filter((i) => matchesTeam(i, team));
  if (list.isPending) return <Loading label="Loading archived issues" rows={3} />;
  if (!rows.length)
    return (
      <EmptyState
        icon={ArchiveIcon}
        className="mt-10"
        title="No archived issues"
        description="Archiving keeps an issue's record off the board."
      />
    );
  return (
    <ul
      className="divide-y divide-subtle rounded-md border border-subtle"
      data-slot="archived-issues"
    >
      {rows.map((i) => (
        <li key={i.id} className="group/row flex h-10 items-center gap-3 px-3 text-sm">
          <ArchiveIcon aria-hidden="true" className="size-4 shrink-0 text-faint" />
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{i.title}</span>
          <span className="w-28 truncate text-xs text-muted-foreground max-md:hidden">
            {i.team_name ?? "Company"}
          </span>
          <span className={HOVER_STRIP}>
            {i.can_edit ? (
              <>
                <IconAction
                  label={`Restore ${i.title}`}
                  tip="Restore"
                  onClick={() => restore.mutate(i)}
                >
                  <ArchiveRestoreIcon aria-hidden="true" />
                </IconAction>
                <IconAction
                  label={`Delete ${i.title}`}
                  tip="Delete"
                  danger
                  onClick={() => onRemove(i)}
                >
                  <Trash2Icon aria-hidden="true" />
                </IconAction>
              </>
            ) : null}
          </span>
        </li>
      ))}
    </ul>
  );
}
